// TSM Nexus — Field Guide content model.
// 13 layers · 144 parameters. Parameter names are the diagnostic checklist
// used inside the audit and are surfaced in the 144-grid exhibit.

export const LAYERS = [
  {
    n: "01",
    name: "Business Context",
    question: "Does the website make sense within the actual business model?",
    params: [
      "Business model clarity",
      "Revenue model alignment",
      "Sales cycle fit",
      "Buying committee coverage",
      "Deal size vs. friction",
      "Geography & market fit",
      "Category maturity",
      "Competitive frame",
      "Channel dependency",
      "Website's role in acquisition",
      "Internal ownership",
    ],
  },
  {
    n: "02",
    name: "Positioning",
    question: "Does it clearly communicate who the company serves and why it matters?",
    params: [
      "Audience specificity",
      "Problem articulation",
      "Category definition",
      "Differentiation claim",
      "Reason to believe",
      "Competitive contrast",
      "Segment relevance",
      "Vocabulary match",
      "Positioning consistency",
      "Above-the-fold comprehension",
      "Five-second test",
    ],
  },
  {
    n: "03",
    name: "Offer",
    question: "Is the commercial proposition compelling?",
    params: [
      "Offer clarity",
      "Perceived value",
      "Pricing transparency",
      "Risk reversal",
      "Packaging logic",
      "Comparison support",
      "Urgency & scarcity honesty",
      "Entry-point offer",
      "Expansion path",
      "Objection pre-handling",
      "Commitment gradient",
    ],
  },
  {
    n: "04",
    name: "Messaging",
    question: "Does the language move the visitor toward understanding and action?",
    params: [
      "Headline specificity",
      "Jargon density",
      "Benefit vs. feature balance",
      "Narrative sequence",
      "Reading level",
      "Voice consistency",
      "Proof adjacency",
      "Microcopy quality",
      "Scannability",
      "Emotional register",
      "Call-to-action language",
    ],
  },
  {
    n: "05",
    name: "UX",
    question: "How much friction exists in the experience?",
    params: [
      "Navigation predictability",
      "Interaction cost",
      "Visual hierarchy",
      "Mobile ergonomics",
      "Form usability",
      "Error recovery",
      "Accessibility baseline",
      "Cognitive load",
      "Dead-end states",
      "Scroll behaviour",
      "Interruption patterns",
    ],
  },
  {
    n: "06",
    name: "Information Architecture",
    question: "Can visitors find and understand what they need?",
    params: [
      "Page inventory logic",
      "Label comprehension",
      "Depth vs. breadth",
      "Cross-linking",
      "Search & findability",
      "Orphan pages",
      "URL semantics",
      "Segment routing",
      "Content duplication",
      "Journey mapping",
      "Footer utility",
    ],
  },
  {
    n: "07",
    name: "Conversion",
    question: "Does the website create clear paths toward action?",
    params: [
      "Primary conversion clarity",
      "CTA prominence",
      "CTA specificity",
      "Path length",
      "Form field count",
      "Field necessity",
      "Friction per step",
      "Secondary conversions",
      "Exit intent handling",
      "Confirmation experience",
      "Follow-up trigger",
    ],
  },
  {
    n: "08",
    name: "Trust",
    question: "Does the website reduce perceived risk?",
    params: [
      "Case study depth",
      "Result specificity",
      "Customer proof",
      "Named references",
      "Team visibility",
      "Authority signals",
      "Third-party validation",
      "Security & compliance",
      "Policy transparency",
      "Consistency of claims",
      "Recency of proof",
    ],
  },
  {
    n: "09",
    name: "Content",
    question: "Does the content support the buying journey?",
    params: [
      "Stage coverage",
      "Depth vs. surface",
      "Editorial quality",
      "Content freshness",
      "Sales enablement value",
      "Objection content",
      "Comparison content",
      "Format variety",
      "Distribution logic",
      "Content-to-CTA linkage",
      "Maintenance load",
    ],
  },
  {
    n: "10",
    name: "SEO",
    question: "Is the website attracting relevant demand?",
    params: [
      "Intent coverage",
      "Keyword-to-page mapping",
      "Technical crawlability",
      "Indexation health",
      "Title & meta quality",
      "Internal link equity",
      "Schema markup",
      "Backlink credibility",
      "Cannibalisation",
      "Branded vs. non-branded mix",
      "Traffic quality",
    ],
  },
  {
    n: "11",
    name: "Performance",
    question: "Does technology support or obstruct the experience?",
    params: [
      "Largest Contentful Paint",
      "Interaction to Next Paint",
      "Cumulative Layout Shift",
      "Time to First Byte",
      "Image weight & format",
      "Script bloat",
      "Third-party tag load",
      "Font loading strategy",
      "Caching & CDN",
      "Mobile network resilience",
      "Broken interactions",
    ],
  },
  {
    n: "12",
    name: "Analytics",
    question: "Can the business actually understand what is happening?",
    params: [
      "Analytics installed correctly",
      "Conversion tracking integrity",
      "Event taxonomy",
      "Form submission tracking",
      "Attribution model",
      "CRM connection",
      "Lead source fidelity",
      "Funnel visibility",
      "Data hygiene",
      "Reporting cadence",
      "Decision usefulness",
    ],
  },
  {
    n: "13",
    name: "Revenue",
    question: "How does all of this ultimately connect to commercial outcomes?",
    params: [
      "Lead volume",
      "Lead quality",
      "Lead-to-opportunity rate",
      "Sales cycle influence",
      "Average customer value",
      "Cost per acquired customer",
      "Paid traffic efficiency",
      "Pipeline contribution",
      "Retention influence",
      "Expansion influence",
      "Return on website spend",
      "Commercial exposure of leaks",
    ],
  },
];

export const TOTAL_PARAMS = LAYERS.reduce((a, l) => a + l.params.length, 0); // 144

export const VALUE_CHAIN = [
  { label: "Demand", leak: "There isn't enough of it, or it's the wrong kind." },
  { label: "Attention", leak: "You pay for it, then lose it in three seconds." },
  { label: "Understanding", leak: "They can't tell what you actually do." },
  { label: "Trust", leak: "They understand you, but don't believe you." },
  { label: "Intent", leak: "They believe you, but aren't moved to act." },
  { label: "Conversion", leak: "They want to act, but the path is hard." },
  { label: "Lead", leak: "The form fires, but the lead is unqualified." },
  { label: "Customer", leak: "The lead arrives, but nobody follows up." },
  { label: "Revenue", leak: "Deals close, but the economics don't work." },
];

export const LEAK_EXAMPLES = [
  {
    title: "Weak positioning",
    cause: "Visitor doesn't understand the value.",
    effect: "Lower engagement.",
  },
  {
    title: "Weak trust",
    cause: "Visitor understands the offer but doesn't believe the company.",
    effect: "Lower intent.",
  },
  {
    title: "Conversion friction",
    cause: "Visitor wants the solution but struggles to take action.",
    effect: "Lost lead.",
  },
  {
    title: "Poor lead qualification",
    cause: "The company generates leads but the wrong ones.",
    effect: "Sales inefficiency.",
  },
  {
    title: "Poor performance",
    cause: "Visitor abandons before experiencing the website.",
    effect: "Lost opportunity.",
  },
];

export const CLASSIFICATION = [
  { key: "IMPACT", q: "How consequential could this be?" },
  { key: "CONFIDENCE", q: "How strong is the evidence?" },
  { key: "EFFORT", q: "How difficult is the correction?" },
  { key: "PRIORITY", q: "What should happen first?" },
];

export const DELIVERABLES = [
  {
    n: "01",
    title: "13-Layer Website Analysis",
    body: "A structured evaluation across 144 parameters.",
  },
  {
    n: "02",
    title: "Revenue Leak Report",
    body: "The highest-priority findings with evidence.",
  },
  {
    n: "03",
    title: "Priority Matrix",
    body: "What to fix now, later and not at all.",
  },
  {
    n: "04",
    title: "Evidence",
    body: "Screenshots, examples and observations from your actual website.",
  },
  {
    n: "05",
    title: "Recommended Solutions",
    body: "Specific actions rather than generic advice.",
  },
  {
    n: "06",
    title: "Revenue Impact Model",
    body: "Where data allows, we model potential commercial impact using transparent assumptions.",
  },
  {
    n: "07",
    title: "Personal Walkthrough",
    body: "A video explaining what we found and why it matters.",
  },
];

export const PROCESS = [
  { n: "01", title: "APPLY", body: "Tell us about your company and website." },
  { n: "02", title: "SELECT", body: "We select 10 businesses that fit the cohort." },
  {
    n: "03",
    title: "DISCOVER",
    body: "We understand your business, customers and website objectives.",
  },
  {
    n: "04",
    title: "AUDIT",
    body: "We run the TSM Nexus 13-layer / 144-parameter analysis.",
  },
  { n: "05", title: "DIAGNOSE", body: "We identify the highest-priority revenue leaks." },
  { n: "06", title: "DELIVER", body: "You receive the report + walkthrough." },
  { n: "07", title: "LEARN", body: "You tell us what was useful and what wasn't." },
];

export const FOR_WHOM = [
  "Founder-led businesses",
  "Measurable website traffic",
  "Visible conversion or revenue leakage",
  "Ecommerce & DTC",
  "B2B & SaaS",
  "Professional services",
];

export const NOT_FOR_WHOM = [
  "You only want a prettier website.",
  "You're looking for free development.",
  "You don't know who your customer is.",
  "Your website has almost no role in your acquisition process.",
  "You want guaranteed revenue numbers.",
  "You don't want critical feedback.",
];

export const BUSINESS_LEAK_CAUSES = [
  "the offer isn't compelling",
  "the market is too small",
  "there isn't enough demand",
  "sales follow-up is poor",
  "customer retention is weak",
  "pricing is wrong",
  "positioning is confused",
];

export const PAGES = [
  { n: "01", id: "page-01", label: "Cover" },
  { n: "02", id: "page-02", label: "The Question" },
  { n: "03", id: "page-03", label: "The Belief" },
  { n: "04", id: "page-04", label: "Revenue Leak" },
  { n: "05", id: "page-05", label: "13 Layers" },
  { n: "06", id: "page-06", label: "144 Parameters" },
  { n: "07", id: "page-07", label: "Founder's Question" },
  { n: "08", id: "page-08", label: "Conversion Leak" },
  { n: "09", id: "page-09", label: "Trust Leak" },
  { n: "10", id: "page-10", label: "Clarity Leak" },
  { n: "11", id: "page-11", label: "Performance Leak" },
  { n: "12", id: "page-12", label: "Business Leak" },
  { n: "13", id: "page-13", label: "What You Receive" },
  { n: "14", id: "page-14", label: "Founding Cohort" },
  { n: "15", id: "page-15", label: "Who It's For" },
  { n: "16", id: "page-16", label: "Who Shouldn't Apply" },
  { n: "17", id: "page-17", label: "The Process" },
  { n: "18", id: "page-18", label: "The Application" },
  { n: "19", id: "page-19", label: "Before You Apply" },
  { n: "20", id: "page-20", label: "The Final Page" },
];
